<template>
  <a-modal
    v-model:visible="props.visible"
    :footer="false"
    centered
    @cancel="emit('update:visible', false)"
    :mask-closable="false"
  >
    <template #title>
      <div class="text-center text-blue">
        <h2 class="text-lg font-semibold">{{ $t('file_upload') }}</h2>
      </div>
    </template>

    <a-divider />

    <a-upload-dragger
      class="white-bg-upload cursor-pointer restricted-height-upload"
      v-model:fileList="fileList"
      :multiple="true"
      :showUploadList="true"
      :customRequest="customRequest"
      :accept="acceptFileTypes"
      :openFileDialogOnClick="fileList.length < maxFiles"
      @preview="handlePreview"
      @change="handleChange"
      @drop="handleDrop"
    >
      <template #removeIcon> </template>

      <template #itemRender="{ file, actions }">
        <div class="flex items-center mt-2">
          <img
            src="@/assets/images/file_upload.jpg"
            alt="file-upload"
            class="w-5 mr-2"
            @click="actions.preview"
          />
          <div class="flex-grow" @click="actions.preview">
            <span :style="file.status === 'error' ? 'color: red' : ''">{{ file.name }}</span>
          </div>
          <a href="javascript:;" @click="actions.remove">
            <CloseCircleFilled class="!text-red-500" />
          </a>
        </div>
      </template>

      <p class="ant-upload-hint mt-4">{{ $t(props.dragMessage) }}</p>
      <p class="ant-upload-hint">
        {{ $t('or') }}
      </p>
      <a-button class="bg-gray mb-4" @click="handleSelectFile" :disabled="disabledAddFile()">{{ $t('select_files') }} </a-button>
    </a-upload-dragger>

    <a-divider />
    <div class="text-center mt-4">
      <a-button type="primary" @click="handleUpload" :disabled="fileList.length == 0">{{ $t('upload') }}</a-button>
    </div>
  </a-modal>
</template>

<script setup lang="ts">
import { ref, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { CloseCircleFilled } from '@ant-design/icons-vue';
import { useFileOpener } from '@/utils';
import { message, type UploadChangeParam } from 'ant-design-vue';
const { openFileInNewTab } = useFileOpener();

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    visible: boolean;
    maxFiles: number;
    currentFiles: any[];
    allowSameName: boolean;
    maxFileMessage: string;
    acceptFileTypes: string;
    dragMessage: string;
    invalidFileTypeMessage: string|null;
  }>(),
  {
    maxFiles: 9999,
    currentFiles: () => [],
    allowSameName: true,
    maxFileMessage: 'MSG_28_5',
    acceptFileTypes: '.pdf',
    dragMessage: 'drop_and_upload_pdf_file',
    invalidFileTypeMessage: null,
  }
);
const emit = defineEmits(['update:visible', 'upload']);

const customRequest = (options: any) => {
  // Implement file upload logic
  setTimeout(() => {
    options.onSuccess({}, options.file);
  }, 0);
};

const fileList = ref<any[]>([]);
let isChanged = false;

const handleChange = async (info: UploadChangeParam) => {
  // If a file other than files in acceptFileTypes is uploaded, remove it from the list
  // acceptFileTypes is a comma-separated string of file extensions like '.jpg,.png,.pdf,.xlsx,.doc,.mp4,.mov'
  let hasNotAcceptedFileType = false;
  fileList.value = info.fileList.filter((file) => {
    const fileExtension = file.name.split('.').pop();
    if (!props.acceptFileTypes.includes(`.${fileExtension}`)) {
        hasNotAcceptedFileType = true;
        return false;
    }
    return true;
  });
  if(hasNotAcceptedFileType && !isChanged){
    message.error(props.invalidFileTypeMessage == null ? t('upload_file_type_error') : t(props.invalidFileTypeMessage));
    hasNotAcceptedFileType = false;
  }
  isChanged = true;

  await nextTick();
  setTimeout(() => {
    // scroll to bottom of ant-upload-list ant-upload-list-text
    const uploadList = document.querySelector('.ant-upload-list');
    if (uploadList) {
      uploadList.scrollTop = uploadList.scrollHeight;
    }
  }, 300);
};

const handleSelectFile = () => {
  isChanged = false;
};

const handleUpload = () => {
  if (fileList.value.length + props.currentFiles.length > props.maxFiles) {
    message.error(t(props.maxFileMessage));
    return;
  }

  if (!props.allowSameName) {
    const allFile = [...props.currentFiles, ...fileList.value];
    const fileNames = new Set<string>();
    for (const file of allFile) {
      if (fileNames.has(file.name)) {
        message.error(t('upload_file_same_name_error'));
        return;
      }
      fileNames.add(file.name);
    }
  }

  // You might want to send the files to a server here
  emit('upload', fileList.value);
  fileList.value = [];
  emit('update:visible', false);
};

const handlePreview = (file: any) => {
  openFileInNewTab(file.originFileObj);
};

const handleDrop = (e: DragEvent) => {
    if (fileList.value.length + props.currentFiles.length > props.maxFiles) {
        message.error(t(props.maxFileMessage));
        fileList.value.pop();
        return;
    }
    if(e.dataTransfer == null || e.dataTransfer.files.length == 0) {
        return;
    }
    let hasNotAcceptedFileType = false;
    for(let i = 0; i < e.dataTransfer.files.length; i++){
        const file = e.dataTransfer.files[i];
        const fileExtension = file.name.split('.').pop();
        if (!props.acceptFileTypes.includes(`.${fileExtension}`)) {
            hasNotAcceptedFileType = true;
            break;
        }
    }
    if(hasNotAcceptedFileType) {
        message.error(props.invalidFileTypeMessage == null ? t('upload_file_type_error') : t(props.invalidFileTypeMessage));
    }
}

const disabledAddFile = () => {
  return fileList.value.length + props.currentFiles.length >= props.maxFiles;
};
</script>
